import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, SealCheck, Storefront, Receipt, QrCode, CreditCard, IdentificationBadge } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import { initials, fmtDate, fmtDateTime, pts } from "@/lib/format";
import { Button } from "@/components/ui/button";
import OneKiteQR from "@/components/OneKiteQR";

export default function AdminCustomerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [error, setError] = useState(false);

  const load = () => { setError(false); api.get(`/admin/customers/${id}`).then((r) => setD(r.data)).catch(() => setError(true)); };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const suspend = async () => {
    try { const { data } = await api.post(`/admin/customers/${id}/suspend`); toast.success(`Account ${data.status}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (error) return (
    <div className="pt-16 text-center space-y-3" data-testid="customer-detail-error">
      <p className="text-muted-foreground">Couldn't load this customer.</p>
      <Button onClick={load} variant="outline" className="rounded-xl border-black/10">Retry</Button>
    </div>
  );
  if (!d) return <div className="text-muted-foreground">Loading…</div>;
  const p = d.profile;
  const verified = p.verification?.email_verified;

  return (
    <div className="space-y-6" data-testid="customer-detail-page">
      <button onClick={() => nav("/admin/customers")} data-testid="back-to-customers" className="text-muted-foreground flex items-center gap-1 text-sm hover:text-foreground"><ArrowLeft size={16} /> Customers</button>

      <div className="card-surface p-6 flex items-start gap-4 flex-wrap">
        <div className="h-16 w-16 rounded-2xl bg-primary/15 flex items-center justify-center font-display font-bold text-primary text-xl overflow-hidden">
          {p.photo ? <img src={p.photo} alt="" className="h-full w-full object-cover" /> : initials(p.full_name)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-display font-bold text-2xl">{p.full_name}</h1>
            <span className={`text-xs px-3 py-1 rounded-full capitalize ${p.status === "active" ? "text-emerald-700 bg-emerald-600/10" : "text-destructive bg-destructive/10"}`}>{p.status}</span>
            {verified && <span className="text-xs text-primary flex items-center gap-1"><SealCheck size={14} weight="fill" /> Verified</span>}
          </div>
          {p.onekite_name && <p className="text-primary text-sm">@{p.onekite_name}</p>}
          <p className="text-xs text-muted-foreground mt-1 font-mono">{p.onekite_id} · {p.phone || "—"}{p.email ? ` · ${p.email}` : ""} · joined {fmtDate(p.created_at)}</p>
        </div>
        <Button data-testid="detail-suspend-customer" onClick={suspend} variant="outline" className="rounded-full border-black/10 text-destructive">
          {p.status === "active" ? "Suspend account" : "Reactivate account"}
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-surface p-5" data-testid="customer-detail-info">
          <p className="text-xs uppercase tracking-wider font-mono text-muted-foreground mb-3 flex items-center gap-1"><IdentificationBadge size={14} /> Personal details & verification</p>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-muted-foreground">Full name</dt><dd className="font-medium">{p.full_name}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">OneKite ID</dt><dd className="font-mono">{p.onekite_id}</dd></div>
            {p.onekite_name && <div className="flex justify-between"><dt className="text-muted-foreground">OneKite name</dt><dd className="text-primary">@{p.onekite_name}</dd></div>}
            <div className="flex justify-between"><dt className="text-muted-foreground">Phone</dt><dd>{p.phone || "—"}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Email</dt><dd>{p.email || "—"}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Verification tier</dt><dd className="font-medium">{p.verification?.tier || "Basic"}</dd></div>
            <div className="flex justify-between items-center">
              <dt className="text-muted-foreground">Email verified</dt>
              <dd>{verified
                ? <span className="text-primary text-xs flex items-center gap-1" data-testid="verified-badge"><SealCheck size={14} weight="fill" /> Verified</span>
                : <span className="text-amber-600 text-xs">Not verified</span>}</dd>
            </div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Joined</dt><dd>{fmtDate(p.created_at)}</dd></div>
          </dl>
        </div>

        <div className="card-surface p-5" data-testid="customer-detail-credentials">
          <p className="text-xs uppercase tracking-wider font-mono text-muted-foreground mb-3 flex items-center gap-1"><QrCode size={14} /> OneKite credentials</p>
          <div className="flex items-start gap-4 flex-wrap">
            {d.qr_token && <div className="text-center"><OneKiteQR value={d.qr_token} size={140} /><p className="text-[10px] text-muted-foreground mt-1">App QR (PERSON)</p></div>}
            <div className="flex-1 min-w-[160px]">
              <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><CreditCard size={14} /> Physical cards</p>
              {(!d.physical_cards || d.physical_cards.length === 0)
                ? <p className="text-sm text-muted-foreground">No physical cards.</p>
                : d.physical_cards.map((c) => (
                    <div key={c.card_reference} className="flex items-center justify-between text-sm py-1" data-testid="customer-detail-card">
                      <span className="font-mono">{c.card_reference}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${c.status === "ACTIVE" ? "text-primary bg-primary/10" : "text-muted-foreground bg-black/5"}`}>{c.status}</span>
                    </div>
                  ))}
            </div>
          </div>
        </div>
      </div>

      <div>
        <p className="text-xs uppercase tracking-wider font-mono text-muted-foreground mb-2 flex items-center gap-1"><Storefront size={14} /> Merchant relationships ({d.relationships.length})</p>
        <div className="card-surface divide-y divide-black/5">
          {d.relationships.length === 0 && <p className="text-sm text-muted-foreground p-4">No merchant relationships yet.</p>}
          {d.relationships.map((r, i) => (
            <div key={i} className="p-3.5 flex items-center justify-between" data-testid="detail-relationship">
              <span className="font-medium text-sm">{r.business}</span>
              <span className="text-primary font-mono text-sm">{pts(r.points)} pts · {r.visits} visits</span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <p className="text-xs uppercase tracking-wider font-mono text-muted-foreground mb-2 flex items-center gap-1"><Receipt size={14} /> Recent transactions</p>
        <div className="card-surface divide-y divide-black/5 max-h-96 overflow-y-auto">
          {d.transactions.length === 0 && <p className="text-sm text-muted-foreground p-4">No transactions.</p>}
          {d.transactions.slice(0, 50).map((t) => (
            <div key={t.id} className="p-3 flex items-center justify-between text-sm" data-testid="detail-transaction">
              <span className="text-muted-foreground capitalize">{t.type} · {fmtDateTime(t.created_at)}</span>
              <span className={t.type === "redemption" ? "text-destructive" : "text-primary"}>{t.type === "redemption" ? `-${pts(t.points_spent)}` : `+${pts(t.points_awarded)}`}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
